/**
 * AvailabilitySettings - Öffnungszeiten & Kalender-Grundeinstellungen
 * Zusammengeführte Einstellungsseite für Studios
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Clock, Save, Calendar, Settings } from 'lucide-react';
import PaymentColorsCard from '@/components/settings/PaymentColorsCard';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AvailabilitySettings = () => {
  const [availability, setAvailability] = useState({});
  const [calendarSettings, setCalendarSettings] = useState({
    calendar_start_hour: 5,
    calendar_end_hour: 23,
    slot_duration: 15,
    booking_window_days: 60,
    cancellation_hours: 24,
    min_advance_hours: 6,
    // Density-Aware Slot-Raster (07/2026)
    density_aware_enabled: true,
    density_threshold_appts: 4,
    density_low_step: 30,
    density_high_step: 15,
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('hours'); // 'hours' or 'calendar'

  const getDayKey = (dayName) => {
    const mapping = {
      'Montag': 'monday',
      'Dienstag': 'tuesday',
      'Mittwoch': 'wednesday',
      'Donnerstag': 'thursday',
      'Freitag': 'friday',
      'Samstag': 'saturday',
      'Sonntag': 'sunday'
    };
    return mapping[dayName] || dayName.toLowerCase();
  };

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch availability
      const availResponse = await axios.get(`${API}/settings/availability`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (availResponse.data) {
        setAvailability(availResponse.data);
      }
      
      // Fetch calendar settings
      const calResponse = await axios.get(`${API}/settings/calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (calResponse.data) {
        setCalendarSettings(prev => ({
          ...prev,
          ...calResponse.data
        }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleDayAvailability = (dayKey) => {
    setAvailability(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        active: !prev[dayKey]?.active,
        slots: prev[dayKey]?.slots || [{ from: '09:00', to: '18:00' }]
      }
    }));
  };

  const updateDaySlot = (dayKey, slotIndex, field, value) => {
    setAvailability(prev => {
      const dayData = prev[dayKey] || { active: true, slots: [{ from: '09:00', to: '18:00' }] };
      const newSlots = [...(dayData.slots || [])];
      newSlots[slotIndex] = { ...newSlots[slotIndex], [field]: value };
      return {
        ...prev,
        [dayKey]: { ...dayData, slots: newSlots }
      };
    });
  };

  const addTimeSlot = (dayKey) => {
    setAvailability(prev => {
      const dayData = prev[dayKey] || { active: true, slots: [] };
      return {
        ...prev,
        [dayKey]: {
          ...dayData,
          slots: [...(dayData.slots || []), { from: '12:00', to: '14:00' }]
        }
      };
    });
  };

  const removeTimeSlot = (dayKey, index) => {
    setAvailability(prev => {
      const dayData = prev[dayKey];
      if (!dayData || !dayData.slots || dayData.slots.length <= 1) return prev;
      const newSlots = dayData.slots.filter((_, i) => i !== index);
      return {
        ...prev,
        [dayKey]: { ...dayData, slots: newSlots }
      };
    });
  };

  const handleSaveAll = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Save availability
      await axios.post(`${API}/settings/availability`, availability, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Save calendar settings
      await axios.post(`${API}/settings/calendar`, calendarSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Alle Einstellungen gespeichert');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock size={24} />
              Öffnungszeiten & Kalender
            </CardTitle>
            <CardDescription>
              Studio-Öffnungszeiten und Kalender-Grundeinstellungen
            </CardDescription>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex gap-2 mt-4 border-b">
          <button
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'hours' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('hours')}
          >
            <Clock size={16} className="inline mr-2" />
            Öffnungszeiten
          </button>
          <button
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              activeTab === 'calendar' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setActiveTab('calendar')}
          >
            <Settings size={16} className="inline mr-2" />
            Kalender-Einstellungen
          </button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Tab: Öffnungszeiten */}
        {activeTab === 'hours' && (
          <>
            <div className="flex gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900" data-testid="global-hours-hint">
              <Clock className="h-4 w-4 flex-shrink-0 mt-0.5 text-blue-600" />
              <p>
                Dies sind die <strong>globalen Standard-Öffnungszeiten</strong> für alle Studios.
                Sie greifen überall, <strong>solange ein Standort keine eigenen Zeiten hinterlegt hat</strong>.
                Legt ein Standort unter <em>Öffnungszeiten (Standorte)</em> abweichende Zeiten fest,
                haben diese <strong>Vorrang</strong> vor diesem Standard.
              </p>
            </div>
            {['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'].map((day) => {
              const dayKey = getDayKey(day);
              const dayData = availability[dayKey] || { active: false, slots: [{ from: '09:00', to: '18:00' }] };
              const slots = dayData.slots || [{ from: '09:00', to: '18:00' }];
              
              return (
                <div key={day} className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Switch 
                        id={`${day}-active`}
                        checked={dayData.active || false}
                        onCheckedChange={() => toggleDayAvailability(dayKey)}
                      />
                      <Label htmlFor={`${day}-active`} className="text-lg font-semibold">
                        {day}
                      </Label>
                    </div>
                    <span className={`text-sm font-medium ${dayData.active ? 'text-green-600' : 'text-red-500'}`}>
                      {dayData.active ? '✓ Geöffnet' : '✗ Geschlossen'}
                    </span>
                  </div>
                  
                  {dayData.active && slots.map((slot, index) => (
                    <div key={index} className="flex items-end gap-4 ml-9">
                      <div className="space-y-2 flex-1">
                        <Label className="text-sm">Von</Label>
                        <Input 
                          type="time" 
                          value={slot.from}
                          onChange={(e) => updateDaySlot(dayKey, index, 'from', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2 flex-1">
                        <Label className="text-sm">Bis</Label>
                        <Input 
                          type="time" 
                          value={slot.to}
                          onChange={(e) => updateDaySlot(dayKey, index, 'to', e.target.value)}
                        />
                      </div>
                      {slots.length > 1 && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => removeTimeSlot(dayKey, index)}
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                  ))}
                  
                  {dayData.active && (
                    <div className="ml-9 pt-2 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="text-xs"
                        onClick={() => addTimeSlot(dayKey)}
                      >
                        + Pause hinzufügen (z.B. Mittagspause)
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
        
        {/* Tab: Kalender-Einstellungen */}
        {activeTab === 'calendar' && (
          <div className="space-y-6">
            {/* Kalender-Farben (oben) */}
            <PaymentColorsCard />

            {/* Kalender-Anzeige */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Calendar size={18} />
                Kalender-Anzeige
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Kalender-Startzeit</Label>
                  <Input 
                    type="number"
                    min="0"
                    max="12"
                    value={calendarSettings.calendar_start_hour}
                    onChange={(e) => setCalendarSettings(prev => ({
                      ...prev,
                      calendar_start_hour: parseInt(e.target.value) || 5
                    }))}
                  />
                  <p className="text-xs text-gray-500">Erste angezeigte Stunde im Kalender</p>
                </div>
                <div className="space-y-2">
                  <Label>Kalender-Endzeit</Label>
                  <Input 
                    type="number"
                    min="12"
                    max="24"
                    value={calendarSettings.calendar_end_hour}
                    onChange={(e) => setCalendarSettings(prev => ({
                      ...prev,
                      calendar_end_hour: parseInt(e.target.value) || 23
                    }))}
                  />
                  <p className="text-xs text-gray-500">Letzte angezeigte Stunde im Kalender</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Slot-Dauer (Minuten)</Label>
                <select 
                  className="w-full border rounded-md p-2"
                  value={calendarSettings.slot_duration}
                  onChange={(e) => setCalendarSettings(prev => ({
                    ...prev,
                    slot_duration: parseInt(e.target.value)
                  }))}
                >
                  <option value="15">15 Minuten</option>
                  <option value="30">30 Minuten</option>
                  <option value="60">60 Minuten</option>
                </select>
                <p className="text-xs text-gray-500">Standard-Zeitintervall im Kalender</p>
              </div>
            </div>

            {/* Density-Aware Slot-Raster (07/2026) */}
            <div className="border rounded-lg p-4 space-y-4" data-testid="density-aware-section">
              <h3 className="font-semibold flex items-center gap-2">
                <Calendar size={18} className="text-amber-500" />
                Density-Aware Slot-Raster
              </h3>
              <p className="text-sm text-gray-600">
                Passt das Slot-Raster automatisch an die Termin-Dichte pro Trainer/Tag an.
                Bei vielen Terminen wird das Raster feiner, damit keine Lücken entstehen.
              </p>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="density_aware_enabled"
                  checked={!!calendarSettings.density_aware_enabled}
                  onChange={(e) => setCalendarSettings(prev => ({
                    ...prev,
                    density_aware_enabled: e.target.checked,
                  }))}
                  data-testid="density-aware-enabled-checkbox"
                  className="w-4 h-4"
                />
                <Label htmlFor="density_aware_enabled" className="cursor-pointer">
                  Density-Aware aktivieren
                </Label>
              </div>

              <div className={`grid grid-cols-3 gap-4 ${calendarSettings.density_aware_enabled ? '' : 'opacity-50 pointer-events-none'}`}>
                <div className="space-y-2">
                  <Label>Schwellwert (Termine/Tag)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="24"
                    value={calendarSettings.density_threshold_appts ?? 4}
                    onChange={(e) => setCalendarSettings(prev => ({
                      ...prev,
                      density_threshold_appts: parseInt(e.target.value) || 1,
                    }))}
                    data-testid="density-threshold-input"
                  />
                  <p className="text-xs text-gray-500">
                    Ab wie vielen Terminen wechselt das Raster.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Raster bei wenigen Terminen</Label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={calendarSettings.density_low_step ?? 30}
                    onChange={(e) => setCalendarSettings(prev => ({
                      ...prev,
                      density_low_step: parseInt(e.target.value),
                    }))}
                    data-testid="density-low-step-select"
                  >
                    <option value="15">15 Minuten</option>
                    <option value="30">30 Minuten</option>
                    <option value="60">60 Minuten</option>
                  </select>
                  <p className="text-xs text-gray-500">
                    Rasterweite wenn wenig los ist.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Raster bei vielen Terminen</Label>
                  <select
                    className="w-full border rounded-md p-2"
                    value={calendarSettings.density_high_step ?? 15}
                    onChange={(e) => setCalendarSettings(prev => ({
                      ...prev,
                      density_high_step: parseInt(e.target.value),
                    }))}
                    data-testid="density-high-step-select"
                  >
                    <option value="5">5 Minuten</option>
                    <option value="10">10 Minuten</option>
                    <option value="15">15 Minuten</option>
                    <option value="30">30 Minuten</option>
                  </select>
                  <p className="text-xs text-gray-500">
                    Feineres Grid wenn viele Termine.
                  </p>
                </div>
              </div>
            </div>
            
            {/* Buchungs-Regeln */}
            <div className="border rounded-lg p-4 space-y-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Clock size={18} />
                Buchungs-Regeln
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Buchungsfenster (Tage)</Label>
                  <Input 
                    type="number"
                    min="7"
                    max="365"
                    value={calendarSettings.booking_window_days}
                    onChange={(e) => setCalendarSettings(prev => ({
                      ...prev,
                      booking_window_days: parseInt(e.target.value) || 60
                    }))}
                  />
                  <p className="text-xs text-gray-500">Wie viele Tage im Voraus kann gebucht werden</p>
                </div>
                <div className="space-y-2">
                  <Label>Mindestvorlauf (Stunden)</Label>
                  <Input 
                    type="number"
                    min="0"
                    max="72"
                    value={calendarSettings.min_advance_hours}
                    onChange={(e) => setCalendarSettings(prev => ({
                      ...prev,
                      min_advance_hours: parseInt(e.target.value) || 6
                    }))}
                  />
                  <p className="text-xs text-gray-500">Mindestzeit vor Terminbeginn für Buchungen</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Stornierungsfrist (Stunden)</Label>
                <Input 
                  type="number"
                  min="0"
                  max="72"
                  value={calendarSettings.cancellation_hours}
                  onChange={(e) => setCalendarSettings(prev => ({
                    ...prev,
                    cancellation_hours: parseInt(e.target.value) || 24
                  }))}
                />
                <p className="text-xs text-gray-500">Bis wie viele Stunden vor dem Termin kann kostenlos storniert werden</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Save Button - Always visible */}
        <div className="pt-4 border-t flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {activeTab === 'hours' ? (
              <p>Tipp: Deaktivieren Sie Tage, an denen Sie geschlossen sind</p>
            ) : (
              <p>Diese Einstellungen gelten für den gesamten Kalender</p>
            )}
          </div>
          <Button onClick={handleSaveAll} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
            <Save size={16} className="mr-2" />
            {loading ? 'Speichern...' : 'Alle Einstellungen speichern'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default AvailabilitySettings;
